import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";

import { trackEvent } from "../../lib/analytics";
import { clearConsent } from "../../lib/consent";
import { DICTS, fill, LANGS, LOCALIZED_LANGS, useI18n, type Lang } from "../../lib/i18n";

// These page types have real /nl, /it, /de, /fr URLs. On any other page (a
// club, a region, admin) the switcher falls back to the original
// client-side-only language change — no dead link risk either way. Guides are
// matched by prefix because every article slug has a per-language route too.
const LOCALIZED_PATHS = new Set(["/", "/clubs", "/partner", "/faq", "/guides"]);

function hasLocaleVariant(base: string): boolean {
  return (
    LOCALIZED_PATHS.has(base) ||
    base.startsWith("/guides/") ||
    base.startsWith("/club/") ||
    base.startsWith("/region/")
  );
}

// Same rule as the root: match any 2-letter prefix, then confirm it against
// LOCALIZED_LANGS, so adding a language never needs a regex edit here too.
function basePath(pathname: string): string {
  const m = pathname.match(/^\/([a-z]{2})(\/.*|$)/);
  if (m && LOCALIZED_LANGS.includes(m[1] as Lang)) return m[2] || "/";
  return pathname;
}

// The mark is a parasol over a lounger, drawn small enough to sit on the
// baseline of the wordmark. It replaces the loose dot the wordmark used to
// end on, which said nothing about what the product is.
function ParasolMark({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 128" className={className} aria-hidden="true">
      <path d="M60 0C33 0 11 17 11 38h98C109 17 87 0 60 0z" fill="#2C4FD6" />
      <rect x="55" y="38" width="10" height="54" rx="5" fill="currentColor" />
      <rect x="8" y="88" width="112" height="15" rx="7" fill="currentColor" />
      <path d="M10 94 L-12 70a8 8 0 0 1 11-10l22 24z" fill="currentColor" />
      <rect x="26" y="103" width="9" height="24" rx="4" fill="currentColor" />
      <rect x="94" y="103" width="9" height="24" rx="4" fill="currentColor" />
    </svg>
  );
}

export function Wordmark({ inverse = false }: { inverse?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-2 ${
        inverse ? "text-[#F2EFE9]" : "text-[#1C2430]"
      }`}
    >
      <ParasolMark className="h-6 w-[22px] shrink-0" />
      <span className="li-serif text-[1.6rem] leading-none tracking-[-0.02em]">sunbedly</span>
    </span>
  );
}

export function LangSwitcher({ inverse = false }: { inverse?: boolean }) {
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const base = basePath(pathname);
  const hasLocaleRoute = hasLocaleVariant(base);

  const pick = (code: Lang) => {
    const target = code === "en" ? base : `/${code}${base === "/" ? "" : base}`;

    // Always record the choice first, then navigate only if that actually
    // changes the URL.
    //
    // The bug this fixes: on an unprefixed URL like /club/x the language comes
    // from localStorage, not from the path. Someone who had picked Dutch there
    // was reading a Dutch page at an English URL. Clicking EN computed a target
    // of /club/x, which is the URL they were already on, so navigate() did
    // nothing and setLang was never reached. The button was genuinely dead, and
    // only on that one combination: no locale in the path, a stored preference,
    // and English as the choice.
    //
    // setLang no-ops when the path carries a locale, because there the URL is
    // the source of truth and the navigation below does the work.
    setLang(code);
    if (hasLocaleRoute && target !== pathname) void navigate({ to: target });
  };

  return (
    // These were 13x17 CSS pixels with an 8px gap: two characters of 11px text
    // and no padding at all. WCAG 2.5.8 asks for 24x24 as a minimum and a
    // finger wants 44. Sam reported the switcher "does not work"; it worked
    // fine, he was missing it. A control that is hard to hit is broken in the
    // only way that counts.
    //
    // The text stays the same size. The padding grows the target to 28x28 and
    // the gap comes down to compensate, so the row is barely wider than it was.
    <div
      className="li-mono flex items-center text-[11px]"
      role="group"
      aria-label={t("footer_langs")}
    >
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          lang={l.code}
          aria-current={lang === l.code ? "true" : undefined}
          title={l.name}
          onClick={() => pick(l.code as Lang)}
          className={`flex min-h-7 min-w-7 items-center justify-center rounded px-1.5 transition-colors ${
            lang === l.code
              ? `underline underline-offset-4 ${inverse ? "text-[#F2EFE9]" : "text-[#2C4FD6]"}`
              : `${
                  inverse
                    ? "text-[#F2EFE9]/60 hover:bg-[#F2EFE9]/10 hover:text-[#F2EFE9]"
                    : "text-[#5B6472] hover:bg-[#1C2430]/8 hover:text-[#1C2430]"
                }`
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}


// Offers the visitor their own language instead of silently switching to it.
// The English URL stays English, which is what a crawler needs, and a Dutch
// visitor still gets one tap to Dutch.
//
// The banner used to mount from a React effect, which meant it appeared a
// beat after first paint and pushed the whole page down 44 px: that insertion
// was the site's entire layout-shift score. Now every language variant is in
// the server HTML, hidden by default, and a tiny inline script in the
// document head picks at most one before first paint by stamping
// data-suggest on <html> (see __root.tsx). Nothing moves after paint.
// Each variant speaks the language it offers: a Dutch reader is served
// "Deze pagina staat ook in het Nederlands", not an English sentence about
// Dutch.
export function LanguageOffer() {
  const { setLang } = useI18n();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  // The attribute lives on <html>, outside React's tree, so dismissing is a
  // plain DOM write. It also survives SPA navigation: dismissed stays
  // dismissed until the next full page load re-runs the head script.
  const hide = () => document.documentElement.removeAttribute("data-suggest");
  const base = basePath(pathname);
  const canRoute = hasLocaleVariant(base);

  return (
    <>
      {LOCALIZED_LANGS.map((code) => {
        const d = DICTS[code];
        const label = LANGS.find((l) => l.code === code)?.name ?? code;
        return (
          /* z-30 because the homepage hero is pulled up 88px to sit behind the
             floating nav pill, and without a stacking order of its own this
             banner disappeared underneath it: the offer to switch language was
             invisible on the one page most likely to need it. */
          <div
            key={code}
            data-offer={code}
            className="lang-offer relative z-30 border-b border-[#1C2430]/10 bg-[#E7E2D6]"
          >
            {/* The two buttons travel together. With `ml-auto` on the dismiss
                button alone, a wrap on a narrow screen stranded it on its own
                right-aligned line, which read as a third, unrelated control. */}
            <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-1 px-5 py-2.5">
              <p className="text-[13px] text-[#1C2430]">{fill(d.lang_offer, { lang: label })}</p>
              <div className="flex items-center gap-4 sm:ml-auto">
                <button
                  type="button"
                  onClick={() => {
                    if (canRoute) void navigate({ to: `/${code}${base === "/" ? "" : base}` });
                    else setLang(code);
                    hide();
                  }}
                  className="li-mono text-[12px] text-[#2C4FD6] underline underline-offset-4"
                >
                  {d.lang_switch}
                </button>
                <button
                  type="button"
                  onClick={hide}
                  className="li-mono text-[12px] text-[#5B6472] hover:text-[#1C2430]"
                >
                  {d.lang_dismiss}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

export function Nav() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <LanguageOffer />
    <header className="sticky top-0 z-40 px-3 pt-3 md:px-5 md:pt-5">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between rounded-full border border-[#1C2430]/10 bg-[#F2EFE9]/85 px-5 shadow-[0_8px_30px_-12px_rgba(28,36,48,0.25)] backdrop-blur-md md:px-7">
        <Link to="/" aria-label="Sunbedly home">
          <Wordmark />
        </Link>
        <nav className="hidden items-center gap-7 md:flex">
          <Link
            to="/clubs"
            className="li-display text-sm font-medium text-[#1C2430] hover:text-[#2C4FD6]"
          >
            {t("nav_explore")}
          </Link>
          <Link
            to="/guides"
            className="li-display text-sm font-medium text-[#1C2430] hover:text-[#2C4FD6]"
          >
            {t("nav_guides")}
          </Link>
          <Link
            to="/partner"
            className="li-mono text-[12px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4 hover:text-[#2C4FD6]"
          >
            {t("nav_partner")}
          </Link>
          <LangSwitcher />
        </nav>
        <button
          type="button"
          aria-label="Menu"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 flex-col items-center justify-center gap-[5px] md:hidden"
        >
          <span className="h-[2px] w-5 bg-[#1C2430]" />
          <span className="h-[2px] w-5 bg-[#1C2430]" />
        </button>
      </div>
      {open ? (
        <div className="mx-auto mt-2 max-w-6xl rounded-3xl border border-[#1C2430]/10 bg-[#F2EFE9] px-5 py-5 shadow-[0_8px_30px_-12px_rgba(28,36,48,0.25)] md:hidden">
          <div className="flex flex-col gap-4">
            <Link
              to="/clubs"
              onClick={() => setOpen(false)}
              className="li-display text-base font-medium text-[#1C2430]"
            >
              {t("nav_explore")}
            </Link>
            <Link
              to="/guides"
              onClick={() => setOpen(false)}
              className="li-display text-base font-medium text-[#1C2430]"
            >
              {t("nav_guides")}
            </Link>
            <Link
              to="/partner"
              onClick={() => setOpen(false)}
              className="li-mono text-[13px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4"
            >
              {t("nav_partner")}
            </Link>
            <LangSwitcher />
          </div>
        </div>
      ) : null}
    </header>
    </>
  );
}

type Destination = { key: string; label: string; country: string; n: number };

// Destination links in the footer are worth real internal-linking weight, so
// they come from the root loader rather than a hardcoded list: a coast appears
// the moment it has enough clubs to be worth landing on, and never before.
export function Footer() {
  const { t } = useI18n();
  const regions = useRouterState({
    select: (s) =>
      (s.matches[0]?.loaderData as { regions?: Destination[] } | undefined)?.regions ?? [],
  });
  const byCountry = regions.reduce<Record<string, Destination[]>>((acc, d) => {
    (acc[d.country] ??= []).push(d);
    return acc;
  }, {});

  return (
    <footer className="border-t border-[#1C2430]/10 bg-[#E7E2D6]">
      {/* Countries and coasts, above the fold of the footer, the way a
          travel directory should link its own inventory. */}
      <div className="mx-auto max-w-6xl border-b border-[#1C2430]/10 px-5 py-12">
        <p className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
          {t("footer_destinations")}
        </p>
        <div className="mt-5 grid gap-8 sm:grid-cols-3 lg:grid-cols-4">
          {Object.entries(byCountry).map(([country, dests]) => (
            <div key={country}>
              <p className="li-serif text-lg text-[#1C2430]">{country}</p>
              <div className="mt-2 flex flex-col gap-1.5">
                {dests.map((d) => (
                  <Link
                    key={d.key}
                    to="/region/$key"
                    params={{ key: d.key }}
                    className="text-sm text-[#5B6472] hover:text-[#2C4FD6]"
                  >
                    {t("footer_beach_clubs_in", { place: d.label })}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-[60ch] text-sm leading-relaxed text-[#5B6472]">
          {t("footer_more_coasts")}
        </p>
      </div>

      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 md:grid-cols-4">
        <div>
          <Wordmark />
          <p className="mt-3 max-w-[32ch] text-sm leading-relaxed text-[#5B6472]">
            {t("footer_tag")}
          </p>
        </div>
        <div>
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
            {t("footer_travelers")}
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <Link to="/clubs" className="text-sm text-[#1C2430] hover:text-[#2C4FD6]">
              {t("nav_explore")}
            </Link>
            <Link to="/guides" className="text-sm text-[#1C2430] hover:text-[#2C4FD6]">
              {t("nav_guides")}
            </Link>
            <Link to="/faq" className="text-sm text-[#1C2430] hover:text-[#2C4FD6]">
              {t("nav_faq")}
            </Link>
            <Link to="/sunbed-prices" className="text-sm text-[#1C2430] hover:text-[#2C4FD6]">
              {t("nav_prices")}
            </Link>
            <Link to="/how-we-verify" className="text-sm text-[#1C2430] hover:text-[#2C4FD6]">
              {t("nav_method")}
            </Link>
            <Link to="/photo-credits" className="text-sm text-[#1C2430] hover:text-[#2C4FD6]">
              {t("nav_photo_credits")}
            </Link>
          </div>
        </div>
        <div>
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
            {t("footer_owners")}
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <Link to="/partner" className="text-sm text-[#1C2430] hover:text-[#2C4FD6]">
              {t("nav_partner")}
            </Link>
          </div>
        </div>
        <div>
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
            {t("footer_contact")}
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <p className="max-w-[26ch] text-sm text-[#5B6472]">{t("footer_contact_note")}</p>
            <a
              href="mailto:hello@sunbedly.com"
              onClick={() => trackEvent("mailto_click", { source: "footer" })}
              className="text-sm text-[#1C2430] hover:text-[#2C4FD6]"
            >
              hello@sunbedly.com
            </a>
            <a
              href="https://www.instagram.com/sunbedly/"
              rel="me noopener"
              target="_blank"
              className="text-sm text-[#1C2430] hover:text-[#2C4FD6]"
            >
              Instagram
            </a>
          </div>
          <p className="li-mono mt-6 text-[11px] uppercase tracking-widest text-[#5B6472]">
            {t("footer_langs")}
          </p>
          <div className="mt-3">
            <LangSwitcher />
          </div>
        </div>
      </div>

      {/* The audit found not a single company name or terms link anywhere on
          the site: everything that looked like proof was borrowed from Google.
          The registration details live on /terms rather than in this bar, so
          the brand a visitor reads is sunbedly and the entity behind it is one
          click away, which is what "easily, directly and permanently
          accessible" asks for.

          The sentence that stays here is the one doing real work: it tells a
          visitor we are not the seller and take no payment, which is what keeps
          a complaint about a beach club with the beach club. */}
      <div className="border-t border-[#1C2430]/10">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-5 md:flex-row md:items-center md:justify-between">
          <p className="li-mono max-w-[62ch] text-[11px] leading-relaxed text-[#5B6472]">
            {t("foot_role")}
          </p>
          <div className="li-mono flex shrink-0 flex-wrap items-center gap-4 text-[11px]">
            <Link to="/terms" className="text-[#5B6472] hover:text-[#2C4FD6]">
              {t("nav_terms")}
            </Link>
            <Link to="/privacy" className="text-[#5B6472] hover:text-[#2C4FD6]">
              {t("nav_privacy")}
            </Link>
            {/* Consent has to be as easy to withdraw as it was to give
                (art. 7(3) GDPR). This brings the banner back and clears any
                _ga cookie already dropped, so a refusal actually stops the
                processing instead of only recording an intention. */}
            <button
              type="button"
              onClick={clearConsent}
              className="text-[#5B6472] underline-offset-2 hover:text-[#2C4FD6] hover:underline"
            >
              {t("ck_settings")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
